import { useForm } from 'react-hook-form'
import { CouponRefundFormItemsType, FormObjectType } from 'src/types/react-hook-form'
import { useModal } from 'src/provider/ModalContextProvider'
import {
  refundBankListQueryOption,
  serviceTermsQueryOption,
  useCustomQuery,
} from 'src/hooks/react-query/useCustomQuery.hook'
import { BankListSpace } from 'src/types/api'
import { NumReplace, regExps } from 'src/utils/regExps_util'
import { ChangeEvent } from 'react'

const useCouponRefundForm = (userName: string) => {
  const { openModal } = useModal()
  const methods = useForm<CouponRefundFormItemsType>({
    mode: 'onChange',
    defaultValues: {
      bankNum: '',
      telecomCertFile: { file: '', name: '' },
      name: '',
      accountNumber: '',
      termsAgree: false,
    },
  })
  const { setValue, setError } = methods
  const { data: termsData } = useCustomQuery(serviceTermsQueryOption({ Type: 1 }))
  const { data: bankListData } = useCustomQuery(refundBankListQueryOption())
  const { BankInfo } = bankListData?.Body!
  const processBankInfo = BankInfo?.map((w: BankListSpace.BankList) => ({
    value: w.BankNum,
    text: w.BankName,
  }))

  const accountNumberOnChangeHandler = (event: { target: { value: string } }) => {
    setValue('accountNumber', NumReplace(event.target.value, 16), { shouldValidate: true })
  }

  const nameOnChangeHandler = (event: { target: { value: string } }) => {
    setValue('name', event.target.value?.slice(0, 100), { shouldValidate: true })
  }
  const bankOnChangeHandler = (event: { target: { value: string } }) => {
    setValue('bankNum', event.target.value, { shouldValidate: true })
  }

  //web
  const telecomCertFileOnChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()

    reader.onloadend = () => {
      const maxSize = 5 * 1024 * 1024
      const acceptFileType = [
        '.pdf',
        '.jpg',
        '.png',
        '.bmp',
        '.gif',
        '.jpeg',
        '.tif',
        '.tiff',
        '.svg',
      ]

      if (!acceptFileType.some((type) => file.name.includes(type))) {
        e.target.value = ''
        setError('telecomCertFile', {
          type: 'validate',
          message: 'pdf 또는 이미지 파일만 업로드 가능합니다.',
        })
        return
      } else if (file?.size > maxSize) {
        e.target.value = ''
        return setError('telecomCertFile', {
          type: 'validate',
          message: '5MB 이하 파일만 업로드 가능합니다.',
        })
      } else {
        // 파일을 base64로 변환하여 state에 저장
        setValue(
          'telecomCertFile',
          { file: reader.result as string, name: file?.name as string },
          { shouldValidate: true, shouldTouch: true },
        )
      }
    }
    reader.readAsDataURL(file)
  }

  const registerAttribute: Record<keyof CouponRefundFormItemsType, Partial<FormObjectType>> = {
    telecomCertFile: {
      type: 'file',
      tag: 'telecomCertFile',
      register_options: {
        onChange: telecomCertFileOnChangeHandler,
        required: true,
        validate: ({ file }: { file: '' }) => {
          return Boolean(file) || false
        },
      },
    },
    bankNum: {
      type: 'text',
      tag: 'bankNum',
      placeholder: '은행을 선택해주세요',
      optionValueList: processBankInfo,
      register_options: {
        onChange: bankOnChangeHandler,
        required: '은행을 선택해주세요.',
      },
    },
    accountNumber: {
      placeholder: '계좌번호를 입력해주세요',
      type: 'tel',
      tag: 'accountNumber',
      register_options: {
        required: '계좌번호를 정확히 입력해주세요.',
        onChange: accountNumberOnChangeHandler,
        minLength: { value: 8, message: '계좌번호를 정확히 입력해주세요.' },
        maxLength: 16,
        inputmode: 'numeric',
      },
    },
    name: {
      placeholder: '예금주 이름을 입력해주세요',
      type: 'text',
      tag: 'name',
      register_options: {
        required: '이름을 정확히 입력해주세요.',
        onChange: nameOnChangeHandler,
        minLength: { value: 2, message: '이름을 정확히 입력해주세요.' },
        maxLength: 100,
        pattern: !!userName
          ? {
              value: new RegExp(`^${userName}$`),
              message:
                '가입자 본인 명의의 계좌가 아닌 경우 환불이 어려울 수 있습니다. 고객센터로 문의해주세요.(1855-4531)',
            }
          : {
              value: regExps.koreanCompleteOrEng,
              message: '이름을 정확히 입력해주세요.',
            },
      },
    },
    termsAgree: {
      tag: 'termsAgree',
      labelValue: '(필수) 환불을 위해 개인정보 수집 및 이용에 동의합니다.',
      register_options: { required: true },
      labelOnClick: () =>
        openModal({
          button: { submit: { t: '닫기' } },
          contents: { t: termsData?.Body?.Title, d: termsData?.Body?.Contents },
        }),
    },
  }

  return {
    methods,
    registerAttribute,
  }
}
export default useCouponRefundForm
